import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Send, Trash2, Sparkles, Lightbulb } from 'lucide-react';

export default function AuthorChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => {
    api.get('/api/admin/ee/chat/history?session_id=default').then(({ data }) => setMessages(data || [])).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim(); if (!msg || sending) return;
    setInput('');
    setMessages((m) => [...m, { message_id: `local_${Date.now()}`, role: 'user', content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post('/api/admin/ee/chat', { message: msg, session_id: 'default' });
      setMessages((m) => [...m, { message_id: data.message_id || `srv_${Date.now()}`, role: 'assistant', content: data.content }]);
    } catch (err) {
      setMessages((m) => [...m, { message_id: `err_${Date.now()}`, role: 'assistant', content: `Error: ${err.message}` }]);
    } finally { setSending(false); }
  };

  const clearHistory = async () => {
    if (!window.confirm('Clear author chat history?')) return;
    await api.delete('/api/admin/ee/chat/history?session_id=default');
    setMessages([]);
  };

  const suggestions = [
    'What are your current rules?',
    'What persona are you using right now?',
    'List the knowledge sources you have access to.',
    'What chapter would you cite if I asked about inherent worth?',
  ];

  return (
    <div className="ee-card flex flex-col h-[70vh]">
      <header className="flex items-center justify-between px-5 py-3 border-b border-ee-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center"><Sparkles size={16} className="text-gold" /></div>
          <div><div className="text-white text-sm font-medium">EE — Unfiltered Mode</div><div className="text-[11px] text-ee-muted">Author chat · can reveal rules &amp; knowledge</div></div>
        </div>
        <button onClick={clearHistory} className="text-xs text-ee-muted hover:text-red-400 inline-flex items-center gap-1"><Trash2 size={12} /> Clear</button>
      </header>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 && (
          <div className="max-w-xl mx-auto text-center mt-6">
            <Lightbulb size={28} className="text-gold mx-auto mb-3" />
            <h3 className="hero-title text-2xl text-white mb-2">Inspect EE</h3>
            <p className="text-ee-text2 text-sm mb-4">Talk to EE without the public-facing filters.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {suggestions.map((s) => <button key={s} onClick={() => setInput(s)} className="ee-card p-3 text-left text-sm text-ee-text2 hover:border-gold/40">{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.role === 'user' ? 'bg-gold/15 border border-gold/30 text-white rounded-br-sm' : 'bg-ee-bg border border-ee-border text-ee-text2 rounded-bl-sm prose-ee'}`}>
              {m.content.split('\n').map((line, i) => <p key={i} className="mb-2 last:mb-0">{line}</p>)}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="bg-ee-bg border border-ee-border rounded-2xl px-4 py-3 dot-anim"><span /><span /><span /></div></div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-ee-border">
        <div className="flex items-center gap-2 bg-ee-bg border border-ee-border rounded-full px-4 focus-within:border-gold">
          <input data-testid="author-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask EE anything (unfiltered)…" className="flex-1 bg-transparent py-2.5 outline-none text-white text-sm" />
          <button data-testid="author-chat-send" type="submit" disabled={sending || !input.trim()} className="w-9 h-9 rounded-full gold-bg flex items-center justify-center text-black disabled:opacity-40"><Send size={14} /></button>
        </div>
      </form>
    </div>
  );
}
